<template>
  <el-dialog
    v-model="visible"
    width="723px"
    :show-close="false"
    class="subscription-manage-modal"
    :close-on-click-modal="true"
    append-to-body
    @close="handleClose"
  >
    <template #header>
      <div class="modal-header">
        <div class="title">订阅管理</div>
        <img :src="images.closePay" alt="关闭" class="close-icon" @click="handleClose" />
      </div>
      <el-tabs v-model="activeTab" class="tabs">
        <el-tab-pane label="订阅" name="subscription" />
        <el-tab-pane label="购买记录" name="records" />
      </el-tabs>
    </template>

    <div
      ref="modalBodyRef"
      v-infinite-scroll="loadMoreRecords"
      class="modal-body"
      :infinite-scroll-disabled="
        activeTab !== 'records' || loadingRecords || loadingMoreRecords || !hasMoreRecords
      "
      :infinite-scroll-distance="100"
    >
      <!-- 订阅列表 -->
      <div v-if="activeTab === 'subscription'" class="tab-panel">
        <div v-if="!loadingSubscription && paidSubscriptions.length > 0" class="subscription-list">
          <div
            v-for="item in paidSubscriptions"
            :key="item.userMembershipId"
            class="subscription-item"
          >
            <div class="info">
              <img :src="images.subscription" alt="订阅" class="icon" />
              <div class="name">
                {{ formatLevelName(item.membershipLevelName) }}-{{ item.membershipPlanName }}
              </div>
            </div>
            <div class="desc">
              <div class="label">有效期至</div>
              <div class="value">{{ formatDateTime(item.expireDateTime) }}</div>
            </div>
          </div>
        </div>

        <InfiniteScrollLoader
          :loading="loadingSubscription"
          :has-more="false"
          :data-length="paidSubscriptions.length"
          :show-empty-state="true"
          empty-text="暂无订阅"
        />
      </div>

      <!-- 购买记录 -->
      <div v-else class="tab-panel">
        <div v-if="!loadingRecords && records.length > 0" class="record-list">
          <div v-for="item in records" :key="item.orderNo" class="record-item">
            <div class="record-header">
              {{ getRecordTitle(item) }}
            </div>
            <div class="record-row">
              <span class="label">价格</span>
              <span class="value">¥{{ item.payAmount || '--' }}</span>
            </div>
            <div class="record-row">
              <span class="label">购买时间</span>
              <span class="value">{{ formatDateTime(item.payTime) }}</span>
            </div>
            <div class="record-row">
              <span class="label">订单编号</span>
              <div class="order-no">
                <span class="value">
                  {{ item.orderNo || '--' }}
                </span>
                <img
                  :src="images.copy"
                  alt="复制"
                  class="copy-icon"
                  @click="handleCopy(item.orderNo)"
                />
              </div>
            </div>
            <div class="record-row">
              <span class="label">支付方式</span>
              <span class="value">
                {{ getPayChannelLabel(item.payChannelCode) }}
              </span>
            </div>
          </div>
        </div>

        <InfiniteScrollLoader
          :loading="loadingRecords || loadingMoreRecords"
          :has-more="hasMoreRecords"
          :data-length="records.length"
          :show-empty-state="true"
          empty-text="暂无购买记录"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { useModalStore } from '@/stores/modal'
import { membershipApi, type UserMembership } from '@/api/membership'
import { paymentApi, type PaymentOrderItem } from '@/api/payment'
import InfiniteScrollLoader from '@/components/InfiniteScrollLoader.vue'
import { images } from '@/assets'
import { copyToClipboard } from '@/utils/clipboard'

const modalStore = useModalStore()
const userStore = useUserStore()

const visible = computed({
  get: () => modalStore.showSubscriptionManageModal,
  set: (val: boolean) => {
    if (!val) modalStore.closeSubscriptionManageModal()
  },
})

const activeTab = ref<'subscription' | 'records'>('subscription')

const subscriptions = ref<UserMembership[]>([])
const loadingSubscription = ref(false)
const paidSubscriptions = computed(() =>
  subscriptions.value.filter((item) => (item.membershipLevelCode || '').toUpperCase() !== 'FREE')
)

const formatLevelName = (name?: string) => (name || '').replace(/会员/g, '版')

const getRecordTitle = (item: PaymentOrderItem) => {
  const kind = (item.productKind || '').toLowerCase()
  const productName = item.productName || ''
  if (kind === 'vip') {
    const productCode = (item.productCode || '').toUpperCase()
    if (productCode.includes('BASIC')) return `潮推手基础版-${productName}`
    if (productCode.includes('STANDARD')) return `潮推手标准版-${productName}`
    if (productCode.includes('ADVANCED')) return `潮推手高级版-${productName}`
    return productName || '潮推手会员购买'
  }
  if (kind === 'points') {
    return `潮推手${productName}潮币充值`
  }
  return productName || '潮推手会员购买'
}

const getPayChannelLabel = (payChannelCode?: string) => {
  const map: Record<string, string> = {
    alipay: '支付宝',
    wechat_pay: '微信支付',
    apple_pay: '苹果支付',
    ADMIN_GIFT: '人工赠送',
    ADMIN_OFFLINE: '线下购买',
  }
  return map[payChannelCode || ''] || '--'
}

const records = ref<PaymentOrderItem[]>([])
const loadingRecords = ref(false)
const loadingMoreRecords = ref(false)
const hasMoreRecords = ref(true)
const modalBodyRef = ref<HTMLElement>()

const pageParams = ref({
  pageNo: 1,
  pageSize: 30,
  total: 0,
})

const formatDateTime = (dateTime?: string | null) => {
  if (!dateTime) return '--'
  const matched = dateTime.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
  if (matched) {
    return `${matched[1]} ${matched[2]}`
  }
  return (
    dateTime
      .replace('T', ' ')
      .replace(/\+\d{2}:\d{2}$/, '')
      .replace(/\.\d+/, '') || '--'
  )
}

const loadSubscriptions = async () => {
  if (!userStore.isLoggedIn) return

  loadingSubscription.value = true
  try {
    const res = await membershipApi.queryMine()
    if (res.code === 0) {
      subscriptions.value = res.data?.list || []
    } else {
      subscriptions.value = []
    }
  } catch (error) {
    console.error('加载订阅列表失败:', error)
    subscriptions.value = []
    ElMessage.error('加载订阅列表失败')
  } finally {
    loadingSubscription.value = false
  }
}

const loadRecords = async (isRefresh = false) => {
  if (!userStore.isLoggedIn) return

  if (isRefresh) {
    loadingRecords.value = true
    pageParams.value.pageNo = 1
    records.value = []
    hasMoreRecords.value = true
  } else {
    if (loadingMoreRecords.value || !hasMoreRecords.value) return
    loadingMoreRecords.value = true
  }

  try {
    const res = await paymentApi.queryOrders({
      pageNo: pageParams.value.pageNo,
      pageSize: pageParams.value.pageSize,
      orderStatus: 2,
    })
    if (res.code === 0 && res.data) {
      const newRecords = res.data.list || []
      const total = res.data.total || 0

      if (isRefresh) {
        records.value = newRecords
      } else {
        records.value.push(...newRecords)
      }

      pageParams.value.total = total
      hasMoreRecords.value = records.value.length < total
    }
  } catch (error) {
    console.error('加载购买记录失败:', error)
    ElMessage.error('加载购买记录失败')
  } finally {
    loadingRecords.value = false
    loadingMoreRecords.value = false
  }
}

// 加载更多购买记录
const loadMoreRecords = async () => {
  if (loadingMoreRecords.value || !hasMoreRecords.value) return

  pageParams.value.pageNo++
  await loadRecords(false)
}

const handleClose = () => {
  modalStore.closeSubscriptionManageModal()
}

const handleCopy = async (text: string | undefined) => {
  if (!text) {
    ElMessage.warning('订单编号为空')
    return
  }
  const success = await copyToClipboard(text)
  if (success) {
    ElMessage.success('复制成功')
  } else {
    ElMessage.error('复制失败，请手动复制')
  }
}

watch(
  () => visible.value,
  (val) => {
    if (val) {
      activeTab.value = 'subscription'
      subscriptions.value = []
      records.value = []
      hasMoreRecords.value = true
      loadSubscriptions()
    }
  }
)

// 监听标签切换，切换到购买记录时重新加载
watch(
  () => activeTab.value,
  (val) => {
    if (val === 'records' && records.value.length === 0) {
      loadRecords(true)
    }
  }
)
</script>

<style lang="scss" scoped>
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .title {
    font-size: var(--font-xxl);
    font-weight: 700;
    color: var(--text-primary);
  }

  .close-icon {
    position: relative;
    cursor: pointer;
    width: 24px;
    height: 24px;
  }
}

.tabs {
  margin-top: 15px;

  // 选中下划线与主题色保持一致
  :deep(.el-tabs__active-bar) {
    font-size: var(--font-xxl) !important;
    background-color: var(--primary-color) !important;
  }
}

.modal-body {
  height: 498px;
  overflow-y: auto;
  overflow-x: hidden;
}

.subscription-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.subscription-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  border-radius: var(--radius-sm);
  background: var(--bg-first);
  border: 1px solid rgba(63, 62, 62, 0.5);
  font-size: var(--font-md);
  color: var(--text-primary);
  .info {
    display: flex;
    align-items: center;
    gap: 4px;
    .icon {
      width: 24px;
      height: 24px;
    }
  }

  .desc {
    text-align: right;

    .label {
      font-size: 12px;
      color: var(--text-six);
    }
  }
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.record-item {
  padding: var(--spacing-md);
  border-radius: 12px 12px 12px 12px;
  background-color: rgba(30, 30, 30, 1);

  .record-header {
    margin-bottom: 18px;
    font-weight: bold;
    color: #f5e6c8;
    font-size: var(--font-lg);
  }

  .record-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: var(--font-md);
    color: var(--text-primary);
    margin-bottom: 11px;

    .order-no {
      display: flex;
      align-items: center;
      gap: 5px;
      img {
        width: 17px;
        height: 17px;
        cursor: pointer;
      }
    }
  }
}
</style>

<style lang="scss">
// 订阅管理弹窗整体样式
.el-dialog.subscription-manage-modal {
  border-radius: 16px !important;
  overflow: hidden;
  background: var(--bg-first) !important;
  border: 1px solid rgba(255, 255, 255, 0.04);

  .el-dialog__header {
    background: var(--bg-first) !important;
    padding: 0 29px !important;
    border-radius: 25px 25px 0 0 !important;
  }

  .el-dialog__body {
    padding: 0 29px !important;
    background: var(--bg-first) !important;
  }
}
</style>
